import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO, subDays, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import { Clock, Filter, Search, Plus, Trash2, Edit, Eye, FileText, Wallet, Package, Scissors, CheckCircle, XCircle } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import { useAppStore } from '../store';
import { ActivityLog } from '../types';
import { supabase } from '../lib/supabase';

const Activity: React.FC = () => {
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<{ id: string; email: string; role: string }[]>([]);
  
  // Filters
  const [filters, setFilters] = useState({
    userId: 'all',
    userRole: 'all',
    period: 'all',
    startDate: '',
    endDate: '',
    actionType: 'all',
    entityType: 'all',
    searchQuery: '',
  });

  // Fetch activity logs
  useEffect(() => {
    const fetchActivityLogs = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('activity_logs')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1000); // Limit to prevent loading too many records
        
        if (error) throw error;
        setActivityLogs(data || []);
      } catch (err: any) {
        console.error('Error fetching activity logs:', err);
        setError(err.message || 'Error al cargar los registros de actividad');
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivityLogs();
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Try to get users via the edge function
        const { data: usersData, error: usersError } = await supabase.functions.invoke('list-users');
        
        if (usersError) throw usersError;
        
        // Format users data
        const formattedUsers = usersData.users.map((user: any) => ({
          id: user.id,
          email: user.email || 'Sin email',
          role: user.role || 'Sin rol',
        }));
        
        setUsers(formattedUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Error al cargar los usuarios');
      }
    };

    fetchUsers();
  }, []);

  // Apply date filters based on period
  useEffect(() => {
    if (filters.period === 'custom') {
      // Don't modify dates for custom period
      return;
    }
    
    const now = new Date();
    let startDate = '';
    
    switch (filters.period) {
      case 'today':
        startDate = format(now, 'yyyy-MM-dd');
        break;
      case '7days':
        startDate = format(subDays(now, 7), 'yyyy-MM-dd');
        break;
      case '30days':
        startDate = format(subDays(now, 30), 'yyyy-MM-dd');
        break;
      case 'month':
        startDate = format(startOfMonth(now), 'yyyy-MM-dd');
        break;
      case 'quarter':
        startDate = format(startOfQuarter(now), 'yyyy-MM-dd');
        break;
      case 'year':
        startDate = format(startOfYear(now), 'yyyy-MM-dd');
        break;
      default:
        // 'all' - no date filtering
        startDate = '';
        break;
    }
    
    setFilters(prev => ({
      ...prev,
      startDate,
      endDate: startDate ? format(now, 'yyyy-MM-dd') : '',
    }));
  }, [filters.period]);

  // Get unique user roles
  const userRoles = useMemo(() => {
    const roles = new Set<string>();
    users.forEach(user => {
      if (user.role) {
        roles.add(user.role);
      }
    });
    return Array.from(roles);
  }, [users]);

  // Get unique entity types
  const entityTypes = useMemo(() => {
    const types = new Set<string>();
    activityLogs.forEach(log => {
      if (log.entity_type) {
        types.add(log.entity_type);
      }
    });
    return Array.from(types);
  }, [activityLogs]);

  // Filter logs
  const filteredLogs = useMemo(() => {
    return activityLogs.filter(log => {
      // Filter by user
      if (filters.userId !== 'all' && log.user_id !== filters.userId) {
        return false;
      }

      // Filter by user role
      if (filters.userRole !== 'all') {
        const user = users.find(u => u.id === log.user_id);
        if (!user || user.role !== filters.userRole) {
          return false;
        }
      }

      // Filter by date range
// Filter by date range
if (filters.startDate && filters.endDate) {
  const timeZone = 'America/Argentina/Buenos_Aires';
  
  // Convertir la fecha del log a zona horaria de Buenos Aires
  const utcLogDate = parseISO(log.created_at);
  const zonedLogDate = toZonedTime(utcLogDate, timeZone);
  
  // Crear fechas de inicio y fin en zona horaria de Buenos Aires
  const startDate = new Date(filters.startDate + 'T00:00:00');
  const endDate = new Date(filters.endDate + 'T23:59:59');
  
  // Convertir las fechas de filtro a UTC para comparación justa
  const utcStartDate = fromZonedTime(startDate, timeZone);
  const utcEndDate = fromZonedTime(endDate, timeZone);
  
  if (utcLogDate < utcStartDate || utcLogDate > utcEndDate) {
    return false;
  }
}

      // Filter by action type
      if (filters.actionType !== 'all' && log.action_type !== filters.actionType) {
        return false;
      }

      // Filter by entity type
      if (filters.entityType !== 'all' && log.entity_type !== filters.entityType) {
        return false;
      }

      // Filter by search query
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const user = users.find(u => u.id === log.user_id);
        const userEmail = user?.email.toLowerCase() || '';
        
        return (
          log.action.toLowerCase().includes(query) ||
          log.details.toLowerCase().includes(query) ||
          userEmail.includes(query)
        );
      }

      return true;
    });
  }, [activityLogs, filters, users]);

  // Get user email by ID
  const getUserEmail = (userId: string): string => {
    const user = users.find(u => u.id === userId);
    return user?.email || 'Usuario desconocido';
  };

  // Get user role by ID
  const getUserRole = (userId: string): string => {
    const user = users.find(u => u.id === userId);
    return user?.role || 'Sin rol';
  };

// Format date to Argentina timezone
// Format date to Argentina timezone
const formatArgentinaDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    
    // Sumar 3 horas para Buenos Aires (UTC-3 pero sumamos porque la DB guarda en UTC)
    const argentinaDate = new Date(date.getTime() + (3 * 60 * 60 * 1000));
    
    return format(argentinaDate, "dd/MM/yyyy HH:mm", { locale: es });
  } catch (error) {
    console.error('Error al formatear fecha:', error);
    return 'Fecha inválida';
  }
};

  // Get action icon
  const getActionIcon = (log: ActivityLog) => {
    const iconProps = { className: "h-5 w-5" };
    
    if (log.action_type === 'create') {
      return <Plus {...iconProps} />;
    } else if (log.action_type === 'update') {
      return <Edit {...iconProps} />;
    } else if (log.action_type === 'delete') {
      return <Trash2 {...iconProps} />;
    } else if (log.action_type === 'view') {
      return <Eye {...iconProps} />;
    }
    
    // Entity-specific icons
    switch (log.entity_type) {
      case 'invoice':
        return <FileText {...iconProps} />;
      case 'payment':
        return <Wallet {...iconProps} />;
      case 'express':
      case 'express_stock':
        return <Package {...iconProps} />;
      case 'sample':
      case 'express_sample':
        return <Scissors {...iconProps} />;
      default:
        return <Clock {...iconProps} />;
    }
  };

  // Get action color class
  const getActionColorClass = (log: ActivityLog): string => {
    if (log.action_type === 'create') {
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    } else if (log.action_type === 'delete') {
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    } else if (log.action_type === 'update') {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    } else {
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      userId: 'all',
      userRole: 'all',
      period: 'all',
      startDate: '',
      endDate: '',
      actionType: 'all',
      entityType: 'all',
      searchQuery: '',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Clock className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Actividad
        </h2>
        <Button
          variant="outline"
          icon={<Filter className="h-5 w-5" />}
          onClick={resetFilters}
        >
          Limpiar Filtros
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader title="Filtros" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-4">
              <Select
                label="Usuario"
                value={filters.userId}
                onChange={(value) => setFilters(prev => ({ ...prev, userId: value }))}
                options={[
                  { value: 'all', label: 'Todos los usuarios' },
                  ...users.map(user => ({
                    value: user.id,
                    label: user.email
                  }))
                ]}
              />

              <Select
                label="Rol"
                value={filters.userRole}
                onChange={(value) => setFilters(prev => ({ ...prev, userRole: value }))}
                options={[
                  { value: 'all', label: 'Todos los roles' },
                  ...userRoles.map(role => ({
                    value: role,
                    label: role
                  }))
                ]}
              />
            </div>

            <div className="space-y-4">
              <Select
                label="Período"
                value={filters.period}
                onChange={(value) => setFilters(prev => ({ ...prev, period: value }))}
                options={[
                  { value: 'all', label: 'Todo el tiempo' },
                  { value: 'today', label: 'Hoy' },
                  { value: '7days', label: 'Últimos 7 días' },
                  { value: '30days', label: 'Últimos 30 días' },
                  { value: 'month', label: 'Este mes' },
                  { value: 'quarter', label: 'Este trimestre' },
                  { value: 'year', label: 'Este año' },
                  { value: 'custom', label: 'Personalizado' },
                ]}
              />

              {filters.period === 'custom' && (
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Desde"
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={filters.startDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                  <Input
                    label="Hasta"
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={filters.endDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <Select
                label="Tipo de Acción"
                value={filters.actionType}
                onChange={(value) => setFilters(prev => ({ ...prev, actionType: value }))}
                options={[
                  { value: 'all', label: 'Todas las acciones' },
                  { value: 'create', label: 'Creación' },
                  { value: 'update', label: 'Actualización' },
                  { value: 'delete', label: 'Eliminación' },
                  { value: 'view', label: 'Visualización' },
                ]}
              />

              <Select
                label="Tipo de Entidad"
                value={filters.entityType}
                onChange={(value) => setFilters(prev => ({ ...prev, entityType: value }))}
                options={[
                  { value: 'all', label: 'Todas las entidades' },
                  ...entityTypes.map(type => ({
                    value: type,
                    label: type
                      .replace('_', ' ')
                      .replace(/\b\w/g, l => l.toUpperCase())
                  }))
                ]}
              />
            </div>
          </div>

          <div className="mt-4 relative">
            <Input
              label="Buscar"
              id="searchQuery"
              name="searchQuery"
              value={filters.searchQuery}
              onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
              placeholder="Buscar en acciones y detalles..."
            />
            <div className="absolute right-3 top-9">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Log */}
      <Card>
        <CardHeader title={`Registros de Actividad (${filteredLogs.length})`} />
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-md text-red-700 dark:text-red-300">
              {error}
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No se encontraron registros de actividad con los filtros seleccionados
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Rol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acción
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Detalles
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fecha y Hora
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {getUserEmail(log.user_id)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                          {getUserRole(log.user_id)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${getActionColorClass(log)}`}>
                          <span className="mr-1">{getActionIcon(log)}</span>
                          {log.action}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {log.details}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatArgentinaDate(log.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Activity;